import { catchError, map, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApplicationData } from '../application.model';
import { ActivatedRoute, Router } from '@angular/router';
import { UserData } from '../../user/user.model';
import { routerTransition } from '../../shared/animations/router.animations';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readOnly, readWrite } from '../../user/user-permission/user-permission.data';
import { BusinessAdvisorPickerComponent } from '../../shared/business-advisor/business-advisor-picker/business-advisor-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationService } from '../application.service';
import { CustomerData } from '../../customer/customer.model';
import { of, Subscription } from 'rxjs';
import { AdvisorData, AdvisorFacade, ApplicationStage, ApplicationType, CommonActionKey, HorizontalListData, MessageService, SignatureMethod, SignatureMethodLabel, SimpleDialogService } from 'common';
import { DocuSignEnvelopeService } from '../../shared/signing-widget/docusign-envelope.service';
import { ApplicationEnvelopeData, EnvelopeStatus, SignatureMethodSwitchOption } from '../../shared/signing-widget/signing-widget.model';
import { ApplicationProfileAdapter } from './application-profile.data';

@UntilDestroy()
@Component({
    selector: 'ifb-application-profile',
    templateUrl: './application-profile.component.html',
    styleUrls: ['./application-profile.component.scss'],
    providers: [ApplicationProfileAdapter],
    animations: routerTransition,
    standalone: false
})
export class ApplicationProfileComponent implements OnInit {
  data: ApplicationProfileComponentData;
  advisorData: AdvisorData;
  canEditAdvisor: boolean;
  canEditSignatureMethod: boolean;
  showSigningCard: boolean;

  otherInfoListData: HorizontalListData[];
  renewalsListData: HorizontalListData[];
  envelope: ApplicationEnvelopeData;
  switchSignatureMethodOptions: SignatureMethodSwitchOption[] = [];

  private hasSignatureMethodPermission: boolean;
  private hasEnvelopePermission: boolean;
  private envelopeSubscription: Subscription;
  private envelopeLoading = false;

  constructor(
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly applicationService: ApplicationService,
    private readonly userPermissionService: UserPermissionService,
    private readonly messageService: MessageService,
    private readonly adapter: ApplicationProfileAdapter,
    private readonly advisorFacade: AdvisorFacade,
    private readonly simpleDialogService: SimpleDialogService,
    private readonly envelopeService: DocuSignEnvelopeService,
  ) { }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe((data: ApplicationProfileComponentData) => {
        this.data = data;
        this.refreshAdvisorData(data?.application?.advisorId);
        this.refreshListData();
        this.refreshEnvelope();
      });

    this.userPermissionService.granted([readWrite('servicing-applications')])
      .pipe(untilDestroyed(this))
      .subscribe(res => this.canEditAdvisor = res);

    this.userPermissionService.granted([readWrite('servicing-applications-signature-method')])
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.hasSignatureMethodPermission = res;
        this.refreshListData();
      });

    this.userPermissionService.granted([readOnly('servicing-docusign-envelopes')])
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.hasEnvelopePermission = res;
        this.refreshEnvelope();
      });
  }

  private refreshAdvisorData(advisorId?: number): void {
    if (!advisorId) {
      this.advisorData = null;
      return;
    }

    this.advisorFacade.getAdvisor$(advisorId)
      .pipe(take(1))
      .subscribe((advisorData: AdvisorData) => this.advisorData = advisorData);
  }

  private refreshListData(): void {
    this.otherInfoListData = this.adapter.adaptOtherInfoListData(this.data);
    this.renewalsListData = this.adapter.adaptRenewalsListData(this.data);
    // A pinned application shows the method at every stage — this is the only place backoffice reports
    // which method signed. An application can also reach Pending Signature without passing intake again,
    // so it is shown unpinned there too: that is the only surface ops have to pin one.
    const awaitingSignature = this.isAwaitingSignature(this.data?.application);
    this.showSigningCard = !!this.data?.application?.signatureMethod || awaitingSignature;
    // Only an application awaiting signature can be moved, and only while something is left to sign.
    this.canEditSignatureMethod = this.hasSignatureMethodPermission
      && awaitingSignature
      && !this.envelopeLoading
      && this.envelope?.status !== EnvelopeStatus.Completed;
    this.switchSignatureMethodOptions = this.canEditSignatureMethod ? this.getSwitchOptions() : [];
  }

  // Loaded whatever the method: an application switched in-house can still hold a completed envelope.
  private refreshEnvelope(): void {
    // Cleared first and the previous request dropped, so a late answer for another application never lands here.
    this.envelopeSubscription?.unsubscribe();
    this.envelope = null;
    this.envelopeLoading = !!this.hasEnvelopePermission && !!this.data?.application?.id;
    this.refreshListData();

    if (!this.envelopeLoading)
      return;

    this.envelopeSubscription = this.envelopeService.getByApplication(this.data.application.id)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.messageService.error(error);
          return of<ApplicationEnvelopeData>(null);
        }),
        untilDestroyed(this))
      .subscribe((envelope: ApplicationEnvelopeData) => {
        this.envelope = envelope;
        this.envelopeLoading = false;
        this.refreshListData();
      });
  }

  // An MSA with no loan is signed in Sales Review as well as Pending Signature, as the API allows.
  private isAwaitingSignature(application: ApplicationData): boolean {
    const isEntityMsa = application?.applicationType === ApplicationType.MSA
      && !application?.loanId
      && !application?.renewalForLoanId;

    return application?.stage === ApplicationStage.PendingSignature
      || (isEntityMsa && application?.stage === ApplicationStage.SalesReview);
  }

  private getSwitchOptions(): SignatureMethodSwitchOption[] {
    return this.getSwitchTargets().map((target: SignatureMethod) => ({
      target,
      label: `Switch to ${SignatureMethodLabel.get(target)}`,
    }));
  }

  // A pinned application only offers the other method; an unpinned one has no "other", so ops pick.
  private getSwitchTargets(): SignatureMethod[] {
    switch (this.data?.application?.signatureMethod) {
      case SignatureMethod.DocuSign:
        return [SignatureMethod.Internal];
      case SignatureMethod.Internal:
        return [SignatureMethod.DocuSign];
      default:
        return [SignatureMethod.Internal, SignatureMethod.DocuSign];
    }
  }

  changeAdvisor(): void {
    BusinessAdvisorPickerComponent.show(this.dialog)
      .pipe(switchMap((data) => {
        if (!data || !data.id) {
          return of();
        }
        return this.applicationService.updateAdvisor(this.data.application.id, data.id)
          .pipe(switchMap(() => this.applicationService.get(this.data.application.id)))
      }))
      .subscribe((applicationData: ApplicationData) => {
        if (applicationData) {
          this.data.application = applicationData;
          this.refreshAdvisorData(applicationData?.advisorId);
          this.refreshListData();
          this.messageService.success(`Advisor successfully changed to ${applicationData.advisorName}`);
        }
      })
  }

  changeSignatureMethod(target: SignatureMethod): void {
    this.simpleDialogService.openSimpleConfirmDialog(this.confirmBody(target), `Switch to ${SignatureMethodLabel.get(target)}?`)
      .pipe(
        switchMap((actionKey: string) => actionKey === CommonActionKey.Confirm
          ? this.applicationService.updateSignatureMethod(this.data.application.id, target).pipe(
            map(() => true),
            catchError((error: HttpErrorResponse) => {
              this.messageService.error(error);
              return of(false);
            }),
            // Re-read either way: a refusal usually means the application moved on since this page loaded.
            switchMap((changed: boolean) => this.applicationService.get(this.data.application.id)
              .pipe(map((application: ApplicationData) => ({ application, changed })))))
          : of<{ application: ApplicationData; changed: boolean }>(null)),
        untilDestroyed(this))
      .subscribe((result: { application: ApplicationData; changed: boolean }) => {
        if (!result) {
          return;
        }

        this.data.application = result.application;
        this.refreshListData();
        this.refreshEnvelope();

        if (result.changed) {
          this.messageService.success(`Signature method changed to ${SignatureMethodLabel.get(result.application.signatureMethod)}`);
        }
      });
  }

  onEditRenewals(): void {
    this.router.navigate(['renewals'], { relativeTo: this.route });
  }

  // An unpinned application has no envelope to void and no in-house signature to re-collect.
  private confirmBody(target: SignatureMethod): string {
    if (!this.data?.application?.signatureMethod) {
      return `This application has no signature method yet. The customer will sign through ${SignatureMethodLabel.get(target)}.`;
    }

    return target === SignatureMethod.Internal
      ? 'The open DocuSign envelope will be voided and the customer will sign in-house.'
      : 'Every owner will be asked to sign again through DocuSign, including owners who already signed in-house.';
  }
}

export interface ApplicationProfileComponentData {
  application: ApplicationData;
  userCurrent?: UserData;
  owners?: CustomerData[];
}
